import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, X, Calendar, MapPin, Edit2, Trash2, Key, Users, EyeOff, Eye, UserCheck, Copy, UserX, DollarSign, CheckCircle, Clock, Banknote, PartyPopper, Sparkles } from 'lucide-react';
import { usePlaylistsContext } from '../contexts/PlaylistsContext';
import { PlaylistForm } from '../PlaylistForm';
import { ServiciosIconsBar } from './ServiciosIcons';
import { EventSpacesDisplay } from './EventSpaces';
import { toast } from 'sonner';
import axios from 'axios';
import useLongPress from '../hooks/useLongPress';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Función para obtener el caché efectivo de un evento
const getEffectiveCache = (event) => {
  // 1. Si tiene cache_acordado, usarlo
  if (event.cache_acordado && parseFloat(event.cache_acordado) > 0) {
    return parseFloat(event.cache_acordado);
  }
  
  // 2. Si tiene presupuesto_total, usarlo
  if (event.presupuesto_total && parseFloat(event.presupuesto_total) > 0) {
    return parseFloat(event.presupuesto_total);
  }
  
  // 3. Calcular desde servicios contratados
  if (event.servicios_contratados && Array.isArray(event.servicios_contratados)) {
    const total = event.servicios_contratados
      .filter(s => s.contratado)
      .reduce((sum, s) => {
        const precio = s.precio_personalizado ?? s.precio_final ?? s.precio_base ?? 0;
        const cantidad = s.cantidad || 1;
        return sum + (precio * cantidad);
      }, 0);
    if (total > 0) return total;
  }
  
  return 0;
};

// Función para formatear moneda
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return null;
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
};

// Sub-componente: badges de estado (cobrado + estado evento) con tap+long-press
// Ubicado fuera del componente principal para evitar re-renders del padre.
const EventStatusBadges = ({ playlist, onToggleCobrado, onOpenCobradoDialog, onToggleEstado }) => {
  // Long-press sobre badge cobrado → abrir diálogo detallado (cambiar monto/método)
  const cobradoLongPress = useLongPress(
    (e) => onOpenCobradoDialog(e, playlist),
    { delay: 600 }
  );

  const isCompletado = playlist.estado === 'completado';

  return (
    <>
      {/* Badge Estado Evento — tap = toggle pendiente/completado */}
      <div
        className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors select-none ${
          isCompletado
            ? 'bg-emerald-900/40 text-emerald-400 hover:bg-emerald-900/60'
            : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700/60'
        }`}
        onClick={(e) => onToggleEstado(e, playlist)}
        title={isCompletado ? 'Click para marcar como pendiente' : 'Click para marcar evento como completado'}
        data-testid={`estado-evento-badge-${playlist.id}`}
      >
        {isCompletado ? (
          <>
            <PartyPopper className="w-3 h-3" />
            Completado
          </>
        ) : (
          <>
            <Clock className="w-3 h-3" />
            Pendiente
          </>
        )}
      </div>

      {/* Badge Cobrado — tap = toggle directo, long-press = diálogo detallado */}
      <div
        {...cobradoLongPress.bindings}
        className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors select-none ${
          playlist.cobrado
            ? 'bg-green-900/40 text-green-400 hover:bg-green-900/60'
            : 'bg-amber-900/40 text-amber-400 hover:bg-amber-900/60'
        } ${cobradoLongPress.isPressing ? 'scale-95' : ''}`}
        onClick={(e) => onToggleCobrado(e, playlist)}
        title={
          playlist.cobrado
            ? 'Click: marcar como pendiente · Mantén pulsado: cambiar monto/método'
            : 'Click: marcar como cobrado · Mantén pulsado: especificar monto/método'
        }
        data-testid={`cobrado-badge-${playlist.id}`}
      >
        {playlist.cobrado ? (
          <>
            <CheckCircle className="w-3 h-3" />
            Cobrado
          </>
        ) : (
          <>
            <DollarSign className="w-3 h-3" />
            Sin cobrar
          </>
        )}
      </div>
    </>
  );
};

export const EventsList = ({
  onSelectPlaylist,
  onEditPlaylist,
  onDeletePlaylist,
  onGenerateAccessCode,
  isCreatePlaylistOpen,
  setIsCreatePlaylistOpen,
  handleCreatePlaylist,
  handleDeletePastEvents,
  handleDeleteAllEvents,
  onRefresh, // Nueva prop para refrescar lista después de duplicar
  user, // Prop para verificar rol del usuario
}) => {
  const {
    filteredPlaylists,
    searchEventTerm,
    setSearchEventTerm,
    hidePastEvents,
    setHidePastEvents,
    showOnlyNoDJ,
    setShowOnlyNoDJ,
    isLoading,
    hasMoreEvents,
    loadMoreEvents,
    totalFilteredCount,
    displayedCount,
    refetch,
  } = usePlaylistsContext();

  // State para el diálogo de duplicar
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedPlaylistToDuplicate, setSelectedPlaylistToDuplicate] = useState(null);
  const [newEventName, setNewEventName] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);

  // State para marcar como cobrado
  const [showCobradoDialog, setShowCobradoDialog] = useState(false);
  const [cobradoEvent, setCobradoEvent] = useState(null);
  const [cobradoMetodo, setCobradoMetodo] = useState('');
  const [cobradoMonto, setCobradoMonto] = useState('');
  const [showOnlyPendienteCobro, setShowOnlyPendienteCobro] = useState(false);

  // Apply local filters on top of context-filtered playlists
  const localFilteredPlaylists = showOnlyPendienteCobro
    ? filteredPlaylists.filter(p => !p.cobrado)
    : filteredPlaylists;

  // Función para marcar evento como cobrado (toggle directo, con Deshacer)
  const handleToggleCobrado = async (e, playlist) => {
    e?.stopPropagation?.();
    const token = localStorage.getItem('token');
    const previousCobrado = !!playlist.cobrado;
    const newCobrado = !previousCobrado;

    try {
      await axios.put(`${API}/api/playlists/${playlist.id}/cobrado`, {
        cobrado: newCobrado,
        metodo_pago: newCobrado ? (playlist.metodo_pago || 'efectivo') : '',
        fecha_cobro: newCobrado ? new Date().toISOString().split('T')[0] : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Optimistic refresh + Deshacer
      const undo = async () => {
        try {
          await axios.put(`${API}/api/playlists/${playlist.id}/cobrado`, {
            cobrado: previousCobrado,
            metodo_pago: previousCobrado ? (playlist.metodo_pago || 'efectivo') : '',
            fecha_cobro: previousCobrado ? (playlist.fecha_cobro || new Date().toISOString().split('T')[0]) : null
          }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Cambio revertido');
          if (refetch) refetch();
          if (onRefresh) onRefresh();
        } catch {
          toast.error('No se pudo revertir');
        }
      };

      toast.success(
        newCobrado ? '✅ Marcado como cobrado' : '⏳ Marcado como pendiente',
        { action: { label: 'Deshacer', onClick: undo }, duration: 5000 }
      );
      if (refetch) refetch();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating cobrado status:', error);
      toast.error('Error al actualizar estado de cobro');
    }
  };

  // Función para marcar evento como completado/pendiente (toggle, con Deshacer)
  const handleToggleEstadoEvento = async (e, playlist) => {
    e?.stopPropagation?.();
    const token = localStorage.getItem('token');
    const previousEstado = playlist.estado || 'pendiente';
    const newEstado = previousEstado === 'completado' ? 'pendiente' : 'completado';

    try {
      await axios.put(`${API}/api/playlists/${playlist.id}`, {
        estado: newEstado,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const undo = async () => {
        try {
          await axios.put(`${API}/api/playlists/${playlist.id}`, {
            estado: previousEstado,
          }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Cambio revertido');
          if (refetch) refetch();
          if (onRefresh) onRefresh();
        } catch {
          toast.error('No se pudo revertir');
        }
      };

      toast.success(
        newEstado === 'completado' ? '🎉 Marcado como completado' : '⏳ Marcado como pendiente',
        { action: { label: 'Deshacer', onClick: undo }, duration: 5000 }
      );
      if (refetch) refetch();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating estado:', error);
      toast.error('Error al actualizar estado del evento');
    }
  };

  // Abrir diálogo de cobrado con opciones
  const openCobradoDialog = (e, playlist) => {
    e.stopPropagation();
    setCobradoEvent(playlist);
    setCobradoMetodo(playlist.metodo_pago || 'efectivo');
    setCobradoMonto(playlist.monto_cobrado || getEffectiveCache(playlist) || '');
    setShowCobradoDialog(true);
  };

  const handleConfirmCobrado = async () => {
    if (!cobradoEvent) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/api/playlists/${cobradoEvent.id}/cobrado`, {
        cobrado: true,
        metodo_pago: cobradoMetodo,
        monto_cobrado: parseFloat(cobradoMonto) || getEffectiveCache(cobradoEvent) || 0,
        fecha_cobro: new Date().toISOString().split('T')[0]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('✅ Evento marcado como cobrado');
      setShowCobradoDialog(false);
      setCobradoEvent(null);
      if (refetch) refetch();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating cobrado status:', error);
      toast.error('Error al actualizar estado de cobro');
    }
  };
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [duplicatePlaylist, setDuplicatePlaylist] = useState(null);
  const [duplicateForm, setDuplicateForm] = useState({
    new_nombre_novios: '',
    new_fecha: '',
    copy_lista_personalizada: true,
    copy_peticiones: false,
    copy_notas: true,
    copy_configuracion: true,
  });

  const handleDuplicateClick = (e, playlist) => {
    e.stopPropagation();
    setDuplicatePlaylist(playlist);
    setDuplicateForm({
      new_nombre_novios: `${playlist.nombre_novios} (copia)`,
      new_fecha: '',
      copy_lista_personalizada: true,
      copy_peticiones: false,
      copy_notas: true,
      copy_configuracion: true,
    });
    setIsDuplicateOpen(true);
  };

  const handleDuplicateSubmit = async () => {
    if (!duplicateForm.new_nombre_novios.trim()) {
      toast.error('Por favor ingresa un nombre para el nuevo evento');
      return;
    }

    setIsDuplicating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/playlists/${duplicatePlaylist.id}/duplicate`,
        {
          new_nombre_novios: duplicateForm.new_nombre_novios.trim(),
          new_fecha: duplicateForm.new_fecha || null,
          copy_lista_personalizada: duplicateForm.copy_lista_personalizada,
          copy_peticiones: duplicateForm.copy_peticiones,
          copy_notas: duplicateForm.copy_notas,
          copy_configuracion: duplicateForm.copy_configuracion,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { copied, new_playlist_id } = response.data;
      toast.success(`✅ Evento duplicado exitosamente`, {
        description: `${copied.canciones} canciones, ${copied.notas} notas copiadas`
      });

      setIsDuplicateOpen(false);
      setDuplicatePlaylist(null);
      
      // Refresh list first
      if (onRefresh) {
        await onRefresh();
      }
      
      // Fetch the new event and open edit modal
      if (new_playlist_id && onEditPlaylist) {
        try {
          const newEventResponse = await axios.get(
            `${API}/api/playlists/${new_playlist_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Small delay to ensure UI has updated
          setTimeout(() => {
            onEditPlaylist(newEventResponse.data);
            toast.info('📝 Puedes modificar los detalles del nuevo evento', { duration: 3000 });
          }, 300);
        } catch (fetchError) {
          console.error('Error fetching new event:', fetchError);
          // Don't show error - the duplicate was successful
        }
      }
    } catch (error) {
      console.error('Error duplicating playlist:', error);
      toast.error('Error al duplicar el evento');
    } finally {
      setIsDuplicating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-4">
      {/* Create Event Button & Dialog - Solo Admin */}
      {user?.role === 'admin' && (
        <div className="mb-4 space-y-2">
          <Dialog open={isCreatePlaylistOpen} onOpenChange={setIsCreatePlaylistOpen}>
            <DialogTrigger asChild>
              <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Crear Nuevo Evento
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl text-white">Crear Playlist para Evento</DialogTitle>
              </DialogHeader>
              <PlaylistForm
                onSubmit={handleCreatePlaylist}
                onCancel={() => setIsCreatePlaylistOpen(false)}
              />
            </DialogContent>
          </Dialog>
          
          {/* Bulk Delete Actions */}
          <div className="flex gap-2">
            <Button 
              onClick={() => {
                if (window.confirm('⚠️ ¿Estás seguro de que quieres eliminar TODOS los eventos pasados (anteriores a hoy)?\n\nEsta acción NO se puede deshacer.')) {
                  handleDeletePastEvents();
                }
              }}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white text-sm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Borrar Eventos Pasados
            </Button>
            <Button 
              onClick={() => {
                if (window.confirm('🚨 PELIGRO: ¿Estás COMPLETAMENTE SEGURO de que quieres eliminar TODOS LOS EVENTOS?\n\nSe borrarán todos los eventos (pasados, presentes y futuros).\n\nEsta acción NO se puede deshacer.')) {
                  if (window.confirm('⚠️ ÚLTIMA CONFIRMACIÓN:\n\n¿Realmente quieres borrar TODOS los eventos?\n\nHaz clic en OK solo si estás 100% seguro.')) {
                    handleDeleteAllEvents();
                  }
                }
              }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Borrar TODOS
            </Button>
          </div>
          
          {/* Keyboard Shortcuts Helper */}
          <div className="mt-3 bg-slate-900/50 border border-slate-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-cyan-400">⌨️ Atajos de Teclado</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-300">Shift + N</kbd>
                <span className="text-slate-400">Nuevo evento</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-300">Shift + T</kbd>
                <span className="text-slate-400">Nueva tarea</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-300">Ctrl + K</kbd>
                <span className="text-slate-400">Búsqueda global</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-300">Shift + ?</kbd>
                <span className="text-slate-400">Ayuda</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar eventos por nombre, lugar o DJ..."
            className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white"
            value={searchEventTerm}
            onChange={(e) => setSearchEventTerm(e.target.value)}
          />
          {searchEventTerm && (
            <button
              onClick={() => setSearchEventTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Filter: Hide Past Events */}
      <div className="mb-4 flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
        <div className="flex items-center gap-3">
          {hidePastEvents ? <EyeOff className="w-5 h-5 text-slate-400" /> : <Eye className="w-5 h-5 text-cyan-400" />}
          <div>
            <Label htmlFor="hide-past-events" className="text-white font-medium cursor-pointer">
              {hidePastEvents ? 'Mostrar eventos pasados' : 'Ocultar eventos pasados'}
            </Label>
            <p className="text-xs text-slate-400 mt-0.5">
              {hidePastEvents ? 'Mostrando solo eventos de ayer en adelante' : 'Mostrando todos los eventos'}
            </p>
          </div>
        </div>
        <Switch
          id="hide-past-events"
          checked={!hidePastEvents}
          onCheckedChange={(checked) => setHidePastEvents(!checked)}
          className="data-[state=checked]:bg-cyan-600"
        />
      </div>

      {/* Filter: Sin DJ Asignado */}
      <div className="mb-4 flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
        <div className="flex items-center gap-3">
          <UserX className={`w-5 h-5 ${showOnlyNoDJ ? 'text-amber-400' : 'text-slate-400'}`} />
          <div>
            <Label htmlFor="show-no-dj" className="text-white font-medium cursor-pointer">
              Sin DJ asignado
            </Label>
            <p className="text-xs text-slate-400 mt-0.5">
              {showOnlyNoDJ ? 'Mostrando solo eventos sin DJ' : 'Mostrando todos los eventos'}
            </p>
          </div>
        </div>
        <Switch
          id="show-no-dj"
          checked={showOnlyNoDJ}
          onCheckedChange={setShowOnlyNoDJ}
          className="data-[state=checked]:bg-amber-500"
          data-testid="filter-no-dj"
        />
      </div>

      {/* Filter: Pendientes de cobro */}
      {user?.role === 'admin' && (
        <div className="mb-4 flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
          <div className="flex items-center gap-3">
            <DollarSign className={`w-5 h-5 ${showOnlyPendienteCobro ? 'text-amber-400' : 'text-slate-400'}`} />
            <div>
              <Label htmlFor="show-pendiente-cobro" className="text-white font-medium cursor-pointer">
                Pendientes de cobro
              </Label>
              <p className="text-xs text-slate-400 mt-0.5">
                {showOnlyPendienteCobro ? 'Mostrando solo eventos sin cobrar' : 'Mostrando todos los eventos'}
              </p>
            </div>
          </div>
          <Switch
            id="show-pendiente-cobro"
            checked={showOnlyPendienteCobro}
            onCheckedChange={setShowOnlyPendienteCobro}
            className="data-[state=checked]:bg-amber-500"
            data-testid="filter-pendiente-cobro"
          />
        </div>
      )}

      {/* Events Count */}
      <div className="text-slate-400 text-sm">
        Mostrando {displayedCount} de {totalFilteredCount} evento{totalFilteredCount !== 1 ? 's' : ''}
      </div>

      {/* Events List */}
      {isLoading ? (
        <div className="grid gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <Skeleton className="h-6 w-3/4 bg-slate-700" />
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 bg-slate-700 rounded-full" />
                        <Skeleton className="h-4 w-32 bg-slate-700" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 bg-slate-700 rounded-full" />
                        <Skeleton className="h-4 w-48 bg-slate-700" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 bg-slate-700 rounded-full" />
                        <Skeleton className="h-4 w-24 bg-slate-700" />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-8 w-8 bg-slate-700 rounded" />
                    <Skeleton className="h-8 w-8 bg-slate-700 rounded" />
                    <Skeleton className="h-8 w-8 bg-slate-700 rounded" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : localFilteredPlaylists.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400 text-lg mb-2">No hay eventos</p>
          <p className="text-slate-500 text-sm">
            {searchEventTerm
              ? "No se encontraron eventos con ese criterio de búsqueda"
              : "Crea tu primer evento para comenzar"}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {localFilteredPlaylists.map((playlist) => (
            <Card
              key={playlist.id}
              className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer overflow-hidden"
              onClick={() => onSelectPlaylist(playlist)}
              data-testid={`event-card-${playlist.id}`}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex-1 min-w-0 overflow-hidden">
                  <h3 className="font-semibold text-white text-base sm:text-lg mb-1.5 truncate max-w-full">
                    {playlist.nombre_novios || 'Sin nombre'}
                  </h3>
                  
                  <div className="space-y-1 text-sm overflow-hidden">
                    {/* Date & Time */}
                    {playlist.fecha_evento && (
                      <div className="flex items-center gap-2 text-slate-400 overflow-hidden">
                        <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate flex-1 min-w-0">
                          {formatDate(playlist.fecha_evento)}
                          {playlist.horario_inicio && (
                            <span className="ml-2 text-cyan-400 font-medium">
                              {playlist.horario_inicio}
                            </span>
                          )}
                        </span>
                      </div>
                    )}

                    {/* Location & Cache & Cobrado */}
                    <div className="flex items-center gap-3 text-slate-400 flex-wrap overflow-hidden">
                      {playlist.lugar_evento && (
                        <div className="flex items-center gap-1.5 min-w-0 max-w-[120px] sm:max-w-none">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{playlist.lugar_evento}</span>
                        </div>
                      )}
                      {user?.role === 'admin' && getEffectiveCache(playlist) > 0 && (
                        <div className="flex items-center gap-1 text-emerald-400">
                          <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="font-medium">{formatCurrency(getEffectiveCache(playlist))}</span>
                        </div>
                      )}
                      {user?.role === 'admin' && (
                        <EventStatusBadges
                          playlist={playlist}
                          onToggleCobrado={handleToggleCobrado}
                          onOpenCobradoDialog={openCobradoDialog}
                          onToggleEstado={handleToggleEstadoEvento}
                        />
                      )}
                    </div>

                    {/* DJ Name & Assistants */}
                    <div className="flex items-center gap-2 text-slate-400 flex-wrap overflow-hidden">
                      <Users className="w-3.5 h-3.5 flex-shrink-0" />
                      <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                        {playlist.nombre_dj ? (
                          <span className="truncate text-cyan-400 max-w-[100px] sm:max-w-none">DJ: {playlist.nombre_dj}</span>
                        ) : (
                          <span className="truncate text-amber-400 flex items-center gap-1">
                            <UserX className="w-3 h-3" />
                            Sin DJ
                          </span>
                        )}
                        {playlist.nombre_asistente && (
                          <span className="text-emerald-400 truncate flex items-center gap-1 max-w-[80px] sm:max-w-none">
                            <UserCheck className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{playlist.nombre_asistente}</span>
                          </span>
                        )}
                        {playlist.nombre_asistente2 && (
                          <span className="text-emerald-400 truncate flex items-center gap-1 max-w-[80px] sm:max-w-none">
                            <UserCheck className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{playlist.nombre_asistente2}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Espacios del evento (compacto) */}
                    {playlist.espacios_evento && playlist.espacios_evento.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap pt-0.5" data-testid={`event-card-spaces-${playlist.id}`}>
                        <EventSpacesDisplay espacios={playlist.espacios_evento} compact />
                        {playlist.espacios_sugeridos_ia && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-300 bg-amber-500/15 border border-amber-500/40 rounded-full px-1.5 py-0.5"
                            title="Espacios detectados por IA, sin confirmar"
                          >
                            <Sparkles className="w-2.5 h-2.5" /> IA — revisar
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Servicios Contratados - Iconos */}
                  {playlist.servicios_contratados && playlist.servicios_contratados.length > 0 && (
                    <div className="mt-2">
                      <ServiciosIconsBar 
                        serviciosContratados={playlist.servicios_contratados} 
                        maxVisible={6}
                        size="sm"
                      />
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    {playlist.custom_list && playlist.custom_list.length > 0 && (
                      <span className="px-2 py-0.5 bg-purple-900/30 text-purple-300 rounded">
                        {playlist.custom_list.length} canciones
                      </span>
                    )}
                    {playlist.ai_recommendations && playlist.ai_recommendations.length > 0 && (
                      <span className="px-2 py-0.5 bg-pink-900/30 text-pink-300 rounded">
                        {playlist.ai_recommendations.length} recomendaciones IA
                      </span>
                    )}
                    {playlist.codigo_acceso_cliente && (
                      <span className="px-2 py-0.5 bg-cyan-900/30 text-cyan-300 rounded flex items-center gap-1">
                        <Key className="w-3 h-3" />
                        Código: {playlist.codigo_acceso_cliente}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Buttons - Horizontal bottom bar */}
                {user?.role === 'admin' && (
                  <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-slate-700/60">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditPlaylist(playlist);
                      }}
                      className="h-8 px-2.5 bg-slate-700/50 border-slate-600 hover:bg-slate-600 text-slate-300"
                      title="Editar evento"
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                      <span className="text-xs hidden sm:inline">Editar</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => handleDuplicateClick(e, playlist)}
                      className="h-8 px-2.5 bg-purple-900/20 border-purple-700/50 hover:bg-purple-900/40 text-purple-400"
                      title="Duplicar evento"
                      data-testid={`duplicate-event-${playlist.id}`}
                    >
                      <Copy className="w-3.5 h-3.5 mr-1.5" />
                      <span className="text-xs hidden sm:inline">Duplicar</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onGenerateAccessCode(playlist);
                      }}
                      className="h-8 px-2.5 bg-cyan-900/20 border-cyan-700/50 hover:bg-cyan-900/40 text-cyan-400"
                      title="Código de acceso"
                    >
                      <Key className="w-3.5 h-3.5 mr-1.5" />
                      <span className="text-xs hidden sm:inline">Código</span>
                    </Button>
                    <div className="flex-1" />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePlaylist(playlist.id);
                      }}
                      className="h-8 px-2.5 bg-red-900/20 border-red-700/50 hover:bg-red-900/40 text-red-400"
                      title="Eliminar evento"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Load More Button */}
      {!isLoading && localFilteredPlaylists.length > 0 && hasMoreEvents && (
        <div className="mt-6 flex justify-center">
          <Button
            onClick={loadMoreEvents}
            className="bg-slate-700 hover:bg-slate-600 text-white border border-slate-600"
          >
            Cargar más eventos ({totalFilteredCount - displayedCount} restantes)
          </Button>
        </div>
      )}

      {/* Diálogo de Duplicar Evento */}
      <Dialog open={isDuplicateOpen} onOpenChange={setIsDuplicateOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Copy className="w-5 h-5 text-purple-400" />
              Duplicar Evento
            </DialogTitle>
          </DialogHeader>
          
          {duplicatePlaylist && (
            <div className="space-y-4">
              <p className="text-slate-400 text-sm">
                Creando copia de: <span className="text-white font-medium">{duplicatePlaylist.nombre_novios}</span>
              </p>

              {/* Nombre del nuevo evento */}
              <div className="space-y-2">
                <Label htmlFor="new-name" className="text-white">Nombre del nuevo evento *</Label>
                <Input
                  id="new-name"
                  value={duplicateForm.new_nombre_novios}
                  onChange={(e) => setDuplicateForm(prev => ({ ...prev, new_nombre_novios: e.target.value }))}
                  placeholder="Ej: Boda Ana & Luis"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              {/* Fecha del nuevo evento */}
              <div className="space-y-2">
                <Label htmlFor="new-date" className="text-white">Fecha del nuevo evento</Label>
                <Input
                  id="new-date"
                  type="date"
                  value={duplicateForm.new_fecha}
                  onChange={(e) => setDuplicateForm(prev => ({ ...prev, new_fecha: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <p className="text-xs text-slate-500">Si dejas vacío, se copiará la fecha original</p>
              </div>

              {/* Opciones de copia */}
              <div className="space-y-3 pt-2 border-t border-slate-700">
                <p className="text-sm font-medium text-slate-300">¿Qué quieres copiar?</p>
                
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="copy-songs"
                    checked={duplicateForm.copy_lista_personalizada}
                    onCheckedChange={(checked) => setDuplicateForm(prev => ({ ...prev, copy_lista_personalizada: checked }))}
                  />
                  <Label htmlFor="copy-songs" className="text-white cursor-pointer">
                    Lista de canciones
                    {duplicatePlaylist.lista_personalizada_generada?.length > 0 && (
                      <span className="text-purple-400 ml-2">({duplicatePlaylist.lista_personalizada_generada.length} canciones)</span>
                    )}
                  </Label>
                </div>

                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="copy-config"
                    checked={duplicateForm.copy_configuracion}
                    onCheckedChange={(checked) => setDuplicateForm(prev => ({ ...prev, copy_configuracion: checked }))}
                  />
                  <Label htmlFor="copy-config" className="text-white cursor-pointer">
                    Configuración (tipo evento, lugar, horarios, DJ asignado...)
                  </Label>
                </div>

                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="copy-notes"
                    checked={duplicateForm.copy_notas}
                    onCheckedChange={(checked) => setDuplicateForm(prev => ({ ...prev, copy_notas: checked }))}
                  />
                  <Label htmlFor="copy-notes" className="text-white cursor-pointer">
                    Notas del equipo
                  </Label>
                </div>

                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="copy-requests"
                    checked={duplicateForm.copy_peticiones}
                    onCheckedChange={(checked) => setDuplicateForm(prev => ({ ...prev, copy_peticiones: checked }))}
                  />
                  <Label htmlFor="copy-requests" className="text-slate-400 cursor-pointer">
                    Peticiones (normalmente NO se quiere copiar)
                  </Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsDuplicateOpen(false)}
              className="bg-slate-700 border-slate-600 hover:bg-slate-600"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDuplicateSubmit}
              disabled={isDuplicating || !duplicateForm.new_nombre_novios.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isDuplicating ? 'Duplicando...' : '📋 Duplicar Evento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para marcar como Cobrado */}
      <Dialog open={showCobradoDialog} onOpenChange={setShowCobradoDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-400" />
              Marcar Evento como Cobrado
            </DialogTitle>
          </DialogHeader>
          
          {cobradoEvent && (
            <div className="space-y-4">
              {/* Info del evento */}
              <div className="bg-slate-700/50 p-3 rounded-lg">
                <p className="font-medium text-white">{cobradoEvent.nombre_novios}</p>
                <p className="text-sm text-slate-400">{cobradoEvent.fecha_evento}</p>
                <p className="text-lg font-semibold text-emerald-400 mt-2">
                  {formatCurrency(getEffectiveCache(cobradoEvent))}
                </p>
              </div>

              {/* Método de pago */}
              <div>
                <Label className="text-slate-300 mb-2 block">Método de Pago</Label>
                <Select value={cobradoMetodo} onValueChange={setCobradoMetodo}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Seleccionar método" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="efectivo">💵 Efectivo</SelectItem>
                    <SelectItem value="transferencia">🏦 Transferencia</SelectItem>
                    <SelectItem value="bizum">📱 Bizum</SelectItem>
                    <SelectItem value="tarjeta">💳 Tarjeta</SelectItem>
                    <SelectItem value="mixto">🔄 Mixto</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Campo de monto */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Importe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={cobradoMonto}
                    onChange={(e) => setCobradoMonto(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="cobrado-monto-input"
                  />
                  <span className="text-slate-400 text-sm">€</span>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCobradoDialog(false);
                    setCobradoEvent(null);
                  }}
                  className="bg-slate-700 border-slate-600"
                >
                  Cancelar
                </Button>
                {cobradoEvent.cobrado ? (
                  <Button
                    onClick={async () => {
                      const token = localStorage.getItem('token');
                      try {
                        await axios.put(`${API}/api/playlists/${cobradoEvent.id}/cobrado`, {
                          cobrado: false,
                          metodo_pago: '',
                          fecha_cobro: null
                        }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('⏳ Evento marcado como pendiente');
                        setShowCobradoDialog(false);
                        setCobradoEvent(null);
                        if (refetch) refetch();
                        if (onRefresh) onRefresh();
                      } catch (error) {
                        toast.error('Error al actualizar');
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Marcar como Pendiente
                  </Button>
                ) : (
                  <Button
                    onClick={handleConfirmCobrado}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirmar Cobrado
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
